import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Company } from "@/api/addressBook.service";
import {
  companyContentMoveService,
  type CompanyContentMoveOperation,
} from "@/api/profiles/companyContentMoveService";
import { CompanyPicker } from "@/components/CompanyPicker/CompanyPicker";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "./ContentMoves.module.css";
import { TABLE_LABELS, downloadManifest, manifestCounts, resolveCompanyName } from "./shared";
import { UndoMoveModal } from "./UndoMoveModal";

const PAGE_SIZE = 25;

/**
 * SOFTSUPP-530 operation-level history: every /apply call that moved content
 * between companies, newest-first, with the rollback manifest downloadable
 * per row and a manifest-restricted Undo. The per-row counterpart lives in
 * the Audit feed; this page answers "what was the move".
 */
export default function ContentMovesPage() {
  const { hasPermission } = usePermissions();
  // Deliberately the dedicated bulk-move permission (staff.profiles_admin
  // only), NOT profiles.read/write — this page exposes cross-firm move
  // history and a tool that rewrites content ownership.
  const canView = hasPermission("profiles.bulk_move");

  const [page, setPage] = useState(1);
  const [companyFilter, setCompanyFilter] = useState<Company | null>(null);
  const [operations, setOperations] = useState<CompanyContentMoveOperation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** companyId -> resolved name (null = looked up, unresolvable — show the uuid). */
  const [names, setNames] = useState<Record<string, string | null>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [undoOperation, setUndoOperation] = useState<CompanyContentMoveOperation | null>(null);
  /** Set by the undo modal on a successful apply; closing then refetches the list. */
  const [undoApplied, setUndoApplied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const offset = (page - 1) * PAGE_SIZE;

  useEffect(() => {
    if (!canView) return;
    let active = true;
    setLoading(true);
    setError(null);
    companyContentMoveService
      .listOperations({
        limit: PAGE_SIZE,
        offset,
        ...(companyFilter ? { companyId: companyFilter.id } : {}),
      })
      .then(result => {
        if (!active) return;
        setOperations(result.operations);
        setTotal(result.total);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load content move history");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canView, offset, companyFilter, reloadKey]);

  // Best-effort name resolution for every company on the page, cached across
  // pages so 25 rows over the same two firms cost two lookups, not fifty.
  // Converges: once every id is in the cache (null included), nothing is
  // missing and the effect is a no-op.
  useEffect(() => {
    const ids = new Set<string>();
    for (const op of operations) {
      ids.add(op.sourceCompanyId);
      ids.add(op.targetCompanyId);
    }
    const missing = [...ids].filter(id => !(id in names));
    if (missing.length === 0) return;

    let active = true;
    void Promise.all(missing.map(async id => [id, await resolveCompanyName(id)] as const)).then(
      entries => {
        if (active) setNames(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      },
    );
    return () => {
      active = false;
    };
  }, [operations, names]);

  if (!canView) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.accessDenied} role="alert">
          <p>You do not have permission to view content move history.</p>
        </div>
      </div>
    );
  }

  function companyLabel(id: string): string {
    return names[id] ?? id;
  }

  function handleFilterChange(company: Company | null) {
    setCompanyFilter(company);
    setPage(1);
  }

  function handleUndoClose() {
    setUndoOperation(null);
    if (undoApplied) {
      setUndoApplied(false);
      // The undo created a new operation (and may have re-pointed rows) —
      // refetch so the new row and the "Undone" badge both show up.
      setReloadKey(key => key + 1);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Content Moves"
        subtitle="Every bulk content move between firms, newest-first, with its rollback manifest."
      />

      <div className={styles.filterRow}>
        <div className={styles.filterPicker}>
          <CompanyPicker
            value={companyFilter}
            onChange={handleFilterChange}
            label="Filter by company"
            placeholder="Search address-book companies by name…"
            listboxId="content-moves-company-listbox"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        {loading ? (
          <p role="status" className={styles.muted}>
            Loading content move history…
          </p>
        ) : error ? (
          <p role="alert" className={styles.errorBanner}>
            {error}
          </p>
        ) : operations.length === 0 ? (
          <p className={styles.emptyState}>
            {companyFilter
              ? "No content move operations involve this company."
              : "No content move operations recorded yet."}
          </p>
        ) : (
          <>
            <ul className={styles.operationList}>
              {operations.map(op => {
                const isExpanded = expanded[op.id] ?? false;
                const skippedCount = op.skipRowIds?.length ?? 0;
                return (
                  <li key={op.id} className={styles.operationCard}>
                    <div className={styles.operationHeader}>
                      <div className={styles.operationMain}>
                        <span className={styles.when}>
                          {new Date(op.createdAt).toLocaleString()}
                        </span>
                        <span className={styles.route}>
                          <Link
                            to={ROUTES.profiles.firmDetail(op.sourceCompanyId)}
                            className={styles.companyLink}
                          >
                            {companyLabel(op.sourceCompanyId)}
                          </Link>
                          <span aria-hidden="true"> → </span>
                          <span className="sr-only"> to </span>
                          <Link
                            to={ROUTES.profiles.firmDetail(op.targetCompanyId)}
                            className={styles.companyLink}
                          >
                            {companyLabel(op.targetCompanyId)}
                          </Link>
                        </span>
                        <span className={styles.rowCount}>
                          {op.totalMoved} {op.totalMoved === 1 ? "row" : "rows"}
                        </span>
                        <span className={styles.actor}>
                          by {op.actor.email ?? op.actor.id ?? "unknown"}
                        </span>
                        {op.undoesMoveId !== null && (
                          <span className={`${styles.badge} ${styles.badgeUndo}`}>Undo</span>
                        )}
                        {op.undoneByMoveId !== null && (
                          <span className={`${styles.badge} ${styles.badgeUndone}`}>Undone</span>
                        )}
                      </div>
                      <div className={styles.operationActions}>
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          aria-label={`Details for move ${op.id}`}
                          aria-expanded={isExpanded}
                          aria-controls={`operation-details-${op.id}`}
                          onClick={() => setExpanded(prev => ({ ...prev, [op.id]: !isExpanded }))}
                        >
                          Details
                        </Button>
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          aria-label={`Undo move ${op.id}`}
                          onClick={() => setUndoOperation(op)}
                        >
                          Undo…
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div id={`operation-details-${op.id}`} className={styles.operationDetails}>
                        <ul className={styles.countsList} aria-label="Rows moved per table">
                          {manifestCounts(op.movedIds).map(([key, count]) => (
                            <li key={key} className={styles.countsItem}>
                              <span>{TABLE_LABELS[key]}</span>
                              <span className={styles.countBadge}>{count}</span>
                            </li>
                          ))}
                        </ul>
                        {skippedCount > 0 && (
                          <p className={styles.detailNote}>
                            Skipped rows: {skippedCount} (left on the source by the operator's
                            conflict decisions)
                          </p>
                        )}
                        {op.undoesMoveId !== null && (
                          <p className={styles.detailNote}>
                            Undoes operation <code className={styles.uuid}>{op.undoesMoveId}</code>
                          </p>
                        )}
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          aria-label={`Download manifest for move ${op.id}`}
                          onClick={() =>
                            downloadManifest({
                              sourceCompanyId: op.sourceCompanyId,
                              targetCompanyId: op.targetCompanyId,
                              movedIds: op.movedIds,
                              totalMoved: op.totalMoved,
                              appliedAt: op.createdAt,
                            })
                          }
                        >
                          Download manifest
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + operations.length}
              totalCount={total}
              itemName="operations"
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {undoOperation && (
        <UndoMoveModal
          operation={undoOperation}
          sourceName={names[undoOperation.sourceCompanyId] ?? null}
          targetName={names[undoOperation.targetCompanyId] ?? null}
          onClose={handleUndoClose}
          onUndone={() => setUndoApplied(true)}
        />
      )}
    </div>
  );
}
